
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ServiceContactSchema, ServiceContactFormValues } from "../services/ServiceContactSchema";
import { CustomFormField } from "../for-pros/FormField";
import { Textarea } from "@/components/ui/textarea";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { submitServiceContact } from "@/utils/serviceContactUtils";

interface ServiceContactFormProps {
  title: string;
  description: string;
}

export const ServiceContactForm = ({ title, description }: ServiceContactFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ServiceContactFormValues>({
    resolver: zodResolver(ServiceContactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ServiceContactFormValues) => {
    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      setIsSubmitting(true);
      console.log('Starting form submission with data:', data);
      
      await submitServiceContact({
        ...data,
        serviceName: title,
        pageUrl: window.location.href
      });
      
      toast({
        title: "Success!",
        description: "We've received your request and will be in touch soon.",
      });

      form.reset();
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem submitting your information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact-form" className="py-12 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-primary/20">
            <CardHeader className="text-center bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="text-2xl font-bold text-primary">{title}</CardTitle>
              <p className="text-muted-foreground">{description}</p>
            </CardHeader>
            <CardContent className="p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <CustomFormField
                    control={form.control}
                    name="name"
                    label="Name"
                  />
                  
                  <CustomFormField
                    control={form.control}
                    name="email"
                    label="Email"
                    type="email"
                  />
                  
                  <CustomFormField
                    control={form.control}
                    name="phone"
                    label="Phone Number"
                    type="tel"
                  />
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message (optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="How can we help you?"
                            className="resize-none"
                            maxLength={100}
                          />
                        </FormControl>
                        <FormMessage />
                        <div className="text-xs text-muted-foreground text-right">
                          {field.value?.length || 0}/100
                        </div>
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Send Request"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export type { ServiceContactFormProps };
